import { useRef, useState, type FormEvent } from "react";
import { Search } from "lucide-react";

import { ActionModal, Field, IconButton } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { pushToast } from "../../app/toasts.js";
import {
  useFriendLookup,
  useSendFriendRequest,
  bandInitials,
  type FriendLookupResult,
} from "../../app/hooks/useFriends.js";
import styles from "./AddFriendModal.module.scss";

/**
 * The add-friend handshake modal — the artist↔artist bridge request flow, in the `ActionModal` shape (PO):
 * one dialog, not two. The email box carries a magnifying-glass button; a lookup populates the result row
 * BELOW it in place — the matched band's avatar + name (a MISS renders "No results found"), so the confirm
 * is a glance at a photo rather than a second screen. The footer is the ActionModal split: Cancel left,
 * Send request right, the confirm disabled until a band is on screen.
 *
 *   1. lookup — `POST /friends/lookup` on the search button (or Enter); a MISS (404) is the designed
 *      "no results" row, never a create.
 *   2. send — `POST /friends/requests` re-sends the email into the recipient's inbox, then closes with a
 *      toast. The guards branch INLINE: 400 (bad email), 403 (not Artist-View tier), 409 (already friends /
 *      ambiguous owner email / self) — server detail verbatim, with a designed fallback. Never a phantom
 *      success (the dialog stays open on a guard).
 */
export function AddFriendModal({ onClose }: { onClose: () => void }) {
  const lookup = useFriendLookup();
  const send = useSendFriendRequest();

  const [email, setEmail] = useState("");
  const [match, setMatch] = useState<FriendLookupResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const firstRef = useRef<HTMLInputElement>(null);

  function onEmailChange(next: string) {
    setEmail(next);
    // A result belongs to the email that produced it — clear it the moment the box changes.
    if (match || notFound || error) {
      setMatch(null);
      setNotFound(false);
      setError(null);
    }
  }

  function onLookup(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotFound(false);
    setMatch(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter the band's email.");
      return;
    }
    lookup.mutate(trimmed, {
      onSuccess: (band) => setMatch(band),
      onError: (err) => {
        // A miss (404) is the designed "no results" row, not an error toast — no ghost-band create.
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        else setError(err.message);
      },
    });
  }

  function onSend() {
    if (!match) return;
    setError(null);
    send.mutate(email.trim(), {
      onSuccess: () => {
        pushToast(`Friend request sent to ${match.bandName || "the band"}.`, "success");
        onClose();
      },
      onError: (err) => setError(guardMessage(err)),
    });
  }

  return (
    <ActionModal
      title="Add a friend"
      confirmLabel="Send request"
      onConfirm={onSend}
      onCancel={onClose}
      loading={send.isPending}
      confirmDisabled={!match}
    >
      <p className={styles.lede}>Friend requests require a band's email address.</p>

      <form className={styles.searchRow} onSubmit={onLookup}>
        <Field
          ref={firstRef}
          className={styles.searchField}
          label="Band email"
          hideLabel
          type="email"
          placeholder="band@email.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />
        <IconButton
          type="submit"
          icon={Search}
          label="Look up band"
          size="field"
          shape="square"
          tone="outline"
          loading={lookup.isPending}
        />
      </form>

      {match && (
        <div className={styles.result}>
          <BandAvatar bandId={match.bandId} avatarMediaId={match.avatarMediaId} initials={bandInitials(match.bandName)} size="md" />
          <p className={styles.resultName}>{match.bandName || "Unnamed band"}</p>
        </div>
      )}

      {notFound && <p className={styles.noResults}>No results found</p>}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </ActionModal>
  );
}

/**
 * The friend-request guard message: the server's ProblemDetails detail verbatim when present, else a
 * designed fallback per status — 403 (not Artist-View tier), 409 (already friends / ambiguous / self), 400
 * (bad email).
 */
function guardMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.message && err.message !== `Request failed (${err.status})`) return err.message;
    if (err.status === 403) return "Only an Artist-View member can send friend requests.";
    if (err.status === 409) return "You're already connected, or a request is already pending.";
    if (err.status === 400) return "Enter a valid email address.";
  }
  return err instanceof Error && err.message ? err.message : "Couldn't send the request.";
}
